/* eslint-disable no-null/no-null */
import { useEffect, useRef, useState } from '../../../../lib/teact/teact';
import { getActions } from '../../../../global';

import type {
  ApiWalletTransactionAttachment,
  ApiWalletTransactionDraftAttachment,
  ApiWalletTransactionMetadata,
  ApiWalletTransactionMetadataDraft,
} from '../../../../api/types';
import type { Account } from '../../../../global/types';
import type { WalletSystemMetadata } from '../../../../util/walletSystemMetadata';
import { MediaType } from '../../../../global/types';

import downloadFile from '../../../../util/downloadFile';
import generateUniqueId from '../../../../util/generateUniqueId';
import { logDebugError } from '../../../../util/logs';
import { resolveAddressCounterparty } from '../../../../util/walletMetadataDirectory';
import { callApiWithThrow } from '../../../../api';
import {
  getTransactionAttachmentPreviewContentType,
  isPreviewableTransactionAttachment,
} from './transactionMetadataFiles';

import useLastCallback from '../../../../hooks/useLastCallback';
import useWalletMetadataDirectory from '../../../../hooks/useWalletMetadataDirectory';

const SYNC_DELAY_MS = 700;

type Options = {
  accountId: string;
  accounts?: Record<string, Account>;
  transactionHash?: string;
  address?: string;
  isEnabled: boolean;
  isDirectoryActive?: boolean;
  enclaveToken?: string;
  isEnclaveSessionValid: boolean;
  systemMetadata?: Partial<WalletSystemMetadata>;
};

export default function useTransactionMetadata({
  accountId,
  accounts,
  transactionHash,
  address,
  isEnabled,
  isDirectoryActive = isEnabled,
  enclaveToken,
  isEnclaveSessionValid,
  systemMetadata,
}: Options) {
  const { openMediaViewer } = getActions();
  const [metadata, setMetadata] = useState<ApiWalletTransactionMetadata>();
  const [draft, setDraft] = useState<ApiWalletTransactionMetadataDraft>();
  const directory = useWalletMetadataDirectory(accountId, accounts, isDirectoryActive);
  const [isLoading, setIsLoading] = useState(false);
  const [attachmentPreviewRevision, setAttachmentPreviewRevision] = useState(0);
  const draftRef = useRef<ApiWalletTransactionMetadataDraft>();
  const metadataRef = useRef<ApiWalletTransactionMetadata>();
  const syncTimeoutRef = useRef<number>();
  const activeKeyRef = useRef('');

  const reload = useLastCallback(async () => {
    if (!isEnabled || !transactionHash) return;
    const key = `${accountId}:${transactionHash}`;
    const initialDraft = draftRef.current;
    const [localDraftResult, metadataResult] = await Promise.allSettled([
      callApiWithThrow('fetchWalletTransactionMetadataDraft', accountId, transactionHash),
      callApiWithThrow('fetchWalletTransactionMetadata', accountId, transactionHash),
    ]);
    if (activeKeyRef.current !== key) return;
    if (localDraftResult.status === 'fulfilled') {
      if (draftRef.current === initialDraft) {
        draftRef.current = localDraftResult.value;
        setDraft(localDraftResult.value);
      }
    } else {
      logDebugError('Transaction metadata local load', localDraftResult.reason);
    }
    if (metadataResult.status === 'fulfilled') {
      metadataRef.current = metadataResult.value?.metadata;
      setMetadata(metadataResult.value?.metadata);
    } else {
      logDebugError('Transaction metadata remote load', metadataResult.reason);
    }
  });

  const sync = useLastCallback(async (token?: string) => {
    if (!isEnabled || !transactionHash) return;
    try {
      await callApiWithThrow('syncWalletTransactionMetadataDraft', accountId, transactionHash, token);
      await reload();
    } catch (error) {
      // Offline and authorization failures are expected here. The local draft remains authoritative.
      logDebugError('Transaction metadata background sync', error);
    }
  });

  const scheduleSync = useLastCallback(() => {
    if (syncTimeoutRef.current) window.clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = window.setTimeout(() => {
      syncTimeoutRef.current = undefined;
      void sync(isEnclaveSessionValid ? enclaveToken : undefined);
    }, SYNC_DELAY_MS);
  });

  const persistDraft = useLastCallback((nextDraft: ApiWalletTransactionMetadataDraft) => {
    draftRef.current = nextDraft;
    setDraft(nextDraft);
    void callApiWithThrow(
      'saveWalletTransactionMetadataDraft',
      nextDraft.account_id,
      nextDraft.transaction_hash,
      nextDraft.description,
      nextDraft.counterparty_name,
      nextDraft.files,
      nextDraft.removed_attachment_ids,
      nextDraft.category_id,
    ).then(scheduleSync).catch((error) => {
      // Keep the in-memory draft even if the local persistence layer is temporarily unavailable.
      logDebugError('Transaction metadata local save', error);
    });
  });

  const getDraftBase = useLastCallback((): ApiWalletTransactionMetadataDraft => {
    return draftRef.current ?? {
      account_id: accountId,
      transaction_hash: transactionHash!,
      description: metadataRef.current?.description ?? '',
      counterparty_name: '',
      category_id: metadataRef.current?.category?.id ?? null,
      files: [],
      removed_attachment_ids: [],
      updated_at: Date.now(),
    };
  });

  const updateDetails = useLastCallback((values: { description?: string; categoryId?: string }) => {
    if (!isEnabled || !transactionHash) return;
    const current = getDraftBase();
    persistDraft({
      ...current,
      description: values.description ?? current.description,
      category_id: values.categoryId !== undefined ? values.categoryId || null : current.category_id,
      updated_at: Date.now(),
    });
  });

  const addFiles = useLastCallback(async (files: File[]) => {
    if (!isEnabled || !transactionHash || !files.length) return;
    const key = `${accountId}:${transactionHash}`;
    try {
      const attachments = await Promise.all(files.map(async (file): Promise<ApiWalletTransactionDraftAttachment> => ({
        client_id: generateUniqueId(),
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
        byte_size: file.size,
        content: new Uint8Array(await file.arrayBuffer()),
      })));
      if (activeKeyRef.current !== key) return;
      const current = getDraftBase();
      persistDraft({
        ...current,
        files: [...current.files, ...attachments],
        updated_at: Date.now(),
      });
    } catch (error) {
      logDebugError('Transaction attachment local save', error);
    }
  });

  const removeExistingAttachment = useLastCallback((attachmentId: string) => {
    const current = getDraftBase();
    persistDraft({
      ...current,
      removed_attachment_ids: [...new Set([...current.removed_attachment_ids, attachmentId])],
      updated_at: Date.now(),
    });
  });

  const removeLocalAttachment = useLastCallback((clientId: string) => {
    const current = getDraftBase();
    persistDraft({
      ...current,
      files: current.files.filter(({ client_id: id }) => id !== clientId),
      updated_at: Date.now(),
    });
  });

  const openAttachmentContent = useLastCallback((
    mediaId: string,
    fileName: string,
    contentType: string,
    content: Uint8Array,
  ) => {
    if (isPreviewableTransactionAttachment(contentType)) {
      const blobType = getTransactionAttachmentPreviewContentType(contentType);
      const mediaUrl = URL.createObjectURL(new Blob([content], { type: blobType }));
      openMediaViewer({
        mediaId,
        mediaType: MediaType.TransactionAttachment,
        mediaUrl,
        mediaName: fileName,
        mediaMimeType: contentType,
        noGhostAnimation: true,
      });
      return;
    }
    downloadFile(fileName, content, contentType);
  });

  const loadAttachmentContent = useLastCallback((attachment: ApiWalletTransactionAttachment) => {
    return callApiWithThrow('downloadWalletTransactionAttachment', accountId, attachment);
  });

  const openAttachment = useLastCallback(async (attachment: ApiWalletTransactionAttachment) => {
    try {
      const result = await loadAttachmentContent(attachment);
      if (!result) return;
      openAttachmentContent(attachment.id, result.fileName, result.contentType, result.content);
    } catch (error) {
      logDebugError('Transaction attachment download', error);
    }
  });

  const openLocalAttachment = useLastCallback((attachment: ApiWalletTransactionDraftAttachment) => {
    try {
      openAttachmentContent(
        attachment.client_id,
        attachment.file_name,
        attachment.content_type,
        attachment.content,
      );
    } catch (error) {
      logDebugError('Transaction local attachment open', error);
    }
  });

  const saveAttachment = useLastCallback(async (attachment: ApiWalletTransactionAttachment) => {
    const result = await loadAttachmentContent(attachment);
    if (result) downloadFile(result.fileName, result.content, result.contentType);
  });

  const saveLocalAttachment = useLastCallback((attachment: ApiWalletTransactionDraftAttachment) => {
    downloadFile(attachment.file_name, attachment.content, attachment.content_type);
  });

  useEffect(() => {
    if (!isEnabled || !transactionHash) return undefined;
    const key = `${accountId}:${transactionHash}`;
    activeKeyRef.current = key;
    draftRef.current = undefined;
    metadataRef.current = undefined;
    setDraft(undefined);
    setMetadata(undefined);
    setIsLoading(true);
    void reload()
      .catch((error) => logDebugError('Transaction metadata load', error))
      .finally(() => {
        if (activeKeyRef.current === key) setIsLoading(false);
      });

    return () => {
      if (activeKeyRef.current === key) activeKeyRef.current = '';
      if (syncTimeoutRef.current) window.clearTimeout(syncTimeoutRef.current);
    };
  }, [accountId, isEnabled, transactionHash]);

  useEffect(() => {
    if (isEnabled && transactionHash && isEnclaveSessionValid && enclaveToken) {
      const key = `${accountId}:${transactionHash}`;
      void sync(enclaveToken).then(() => {
        if (activeKeyRef.current === key) setAttachmentPreviewRevision((revision) => revision + 1);
      });
    }
  }, [accountId, enclaveToken, isEnabled, isEnclaveSessionValid, transactionHash]);

  const removedAttachmentIds = draft?.removed_attachment_ids ?? [];
  const counterparty = resolveAddressCounterparty(directory.counterparties, address,
    accounts?.[accountId]?.byChain.tron?.address) ?? systemMetadata?.counterparty;
  const categoryId = (draft?.category_id !== undefined ? draft.category_id ?? '' : metadata?.category?.id ?? '')
    || systemMetadata?.category?.id
    || '';
  return {
    editorProps: {
      accountId,
      transactionHash,
      address,
      counterparties: directory.counterparties,
      ...(systemMetadata ? { systemMetadata } : undefined),
    },
    description: draft?.description ?? metadata?.description ?? '',
    counterparty,
    categoryId,
    categories: directory.categories.filter((category) => (
      category.account_id === accountId || (!category.account_id
        && category.owner_address === accounts?.[accountId]?.byChain.tron?.address)
    )),
    counterparties: directory.counterparties,
    attachments: metadata?.attachments.filter(({ id }) => !removedAttachmentIds.includes(id)) ?? [],
    localAttachments: draft?.files ?? [],
    isLoading,
    attachmentPreviewRevision,
    updateDetails,
    addFiles,
    removeExistingAttachment,
    removeLocalAttachment,
    openAttachment,
    loadAttachmentContent,
    openLocalAttachment,
    saveAttachment,
    saveLocalAttachment,
  };
}
